import lessonLibraryService from "./lessonLibraryService";
import professionalDevelopmentService from "./professionalDevelopmentService";

/**
 * Configuration for different comment service types
 */
const SERVICE_CONFIGS = {
  lesson: {
    service: lessonLibraryService,
    fieldMappings: {
      lessonId: "lesson_id", // Maps internal lessonId to API field
      blockId: "block_id",
      comment: "comment",
    },
    methods: {
      getComments: "getLessonComments",
      createComment: "createLessonComment",
      updateComment: "updateLessonComment",
      deleteComment: "deleteLessonComment",
      likeComment: "likeLessonComment",
      getContentComments: "getLessonContentComments",
      createContentComment: "createContentComment",
      updateContentComment: "updateLessonContentComment",
      deleteContentComment: "deleteLessonContentComment",
      likeContentComment: "likeLessonContentComment",
    },
  },
  professionalDevelopment: {
    service: professionalDevelopmentService,
    fieldMappings: {
      lessonId: "instructional_strategies_id", // Maps lessonId to professional development field
      blockId: "block_id",
      comment: "comment",
    },
    methods: {
      getComments: "getProfessionalDevelopmentComments",
      createComment: "createProfessionalDevelopmentComment",
      updateComment: "updateProfessionalDevelopmentComment",
      deleteComment: "deleteProfessionalDevelopmentComment",
      likeComment: "likeProfessionalDevelopmentComment",
      getContentComments: "getContentComments",
      createContentComment: "createContentComment",
      updateContentComment: "updateContentComment",
      deleteContentComment: "deleteContentComment",
      likeContentComment: "likeContentComment",
    },
  },
};

/**
 * Validates the payload structure
 * @param {Object} payload - The payload to validate
 * @param {Array} requiredFields - Required field names
 * @throws {Error} If validation fails
 */
const validatePayload = (payload, requiredFields = []) => {
  if (!payload || typeof payload !== "object") {
    throw new Error("Payload must be a valid object");
  }

  for (const field of requiredFields) {
    if (payload[field] === undefined || payload[field] === null) {
      throw new Error(`Missing required field: ${field}`);
    }
  }
};

/**
 * Transforms payload fields according to service configuration
 * @param {Object} payload - Original payload
 * @param {Object} fieldMappings - Field mapping configuration
 * @returns {Object} Transformed payload
 */
const transformPayload = (payload, fieldMappings) => {
  const transformed = {};

  Object.entries(payload).forEach(([key, value]) => {
    // Use mapped field name if exists, otherwise use original key
    const mappedKey = fieldMappings[key] || key;
    transformed[mappedKey] = value;
  });

  return transformed;
};

/**
 * Creates a robust API call wrapper with error handling
 * @param {Function} apiMethod - The API method to call
 * @param {string} operation - Description of the operation for error context
 * @returns {Function} Wrapped API method
 */
const createApiWrapper = (apiMethod, operation) => {
  return async (...args) => {
    try {
      const result = await apiMethod(...args);
      return result;
    } catch (error) {
      console.error(`Error during ${operation}:`, error);

      // Re-throw with additional context but preserve original error
      const enhancedError = new Error(
        `Failed to ${operation}: ${error.message}`
      );
      enhancedError.originalError = error;
      enhancedError.operation = operation;
      throw enhancedError;
    }
  };
};

/**
 * Creates a comment service with the specified configuration
 * @param {string} serviceType - Type of service ('lesson' or 'professionalDevelopment')
 * @returns {Object} Comment service interface
 */
const createCommentService = (serviceType) => {
  const config = SERVICE_CONFIGS[serviceType];

  if (!config) {
    throw new Error(
      `Unknown service type: ${serviceType}. Available types: ${Object.keys(SERVICE_CONFIGS).join(", ")}`
    );
  }

  const { service, fieldMappings, methods } = config;

  return {
    // Legacy comment methods (for backward compatibility)
    getComments: createApiWrapper((lessonId) => {
      validatePayload({ lessonId }, ["lessonId"]);
      return service[methods.getComments](lessonId);
    }, "get comments"),

    createComment: createApiWrapper((payload) => {
      validatePayload(payload, ["comment", "lessonId"]);
      const transformedPayload = transformPayload(payload, fieldMappings);
      return service[methods.createComment](transformedPayload);
    }, "create comment"),

    updateComment: createApiWrapper((commentId, payload) => {
      if (!commentId) throw new Error("Comment ID is required");
      validatePayload(payload, ["comment"]);
      const transformedPayload = transformPayload(payload, fieldMappings);
      return service[methods.updateComment](commentId, transformedPayload);
    }, "update comment"),

    deleteComment: createApiWrapper((commentId) => {
      if (!commentId) throw new Error("Comment ID is required");
      return service[methods.deleteComment](commentId);
    }, "delete comment"),

    likeComment: createApiWrapper((commentId) => {
      if (!commentId) throw new Error("Comment ID is required");
      return service[methods.likeComment](commentId);
    }, "like comment"),

    // Content comment methods (main interface used by CommentPopover)
    getContentComments: createApiWrapper((lessonId) => {
      validatePayload({ lessonId }, ["lessonId"]);
      return service[methods.getContentComments](lessonId);
    }, "get content comments"),

    createContentComment: createApiWrapper((payload) => {
      validatePayload(payload, ["comment", "lessonId", "blockId"]);
      const transformedPayload = transformPayload(payload, fieldMappings);
      return service[methods.createContentComment](transformedPayload);
    }, "create content comment"),

    updateContentComment: createApiWrapper((commentId, payload) => {
      if (!commentId) throw new Error("Comment ID is required");
      validatePayload(payload, ["comment"]);
      const transformedPayload = transformPayload(payload, fieldMappings);
      return service[methods.updateContentComment](
        commentId,
        transformedPayload
      );
    }, "update content comment"),

    deleteContentComment: createApiWrapper((commentId) => {
      if (!commentId) throw new Error("Comment ID is required");
      return service[methods.deleteContentComment](commentId);
    }, "delete content comment"),

    likeContentComment: createApiWrapper((commentId) => {
      if (!commentId) throw new Error("Comment ID is required");
      return service[methods.likeContentComment](commentId);
    }, "like content comment"),
  };
};

// Factory functions for backward compatibility
export const createLessonCommentService = () => createCommentService("lesson");

export const createProfessionalDevelopmentCommentService = () =>
  createCommentService("professionalDevelopment");

// Export the generic factory for potential future use
export { createCommentService };

// Export service configurations for testing or advanced usage
export { SERVICE_CONFIGS };
